"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from '../structure/carousel';
import { ReviewSchema } from '../foundation/schema';
import { getContentfulEntriesByType, getContentfulReviewsSchema } from './contentful.delivery';
import { usePixelatedConfig } from '../config/config.client';
import './contentful.reviews.css';
ContentfulReviewsCarousel.propTypes = {
    /** Contentful entry type used to build the carousel cards. */
    reviewContentType: PropTypes.string.isRequired,
    /** Name of the item or service being reviewed for schema generation. */
    itemName: PropTypes.string.isRequired,
    /** Schema type of the reviewed item. */
    itemType: PropTypes.string,
    /** Publisher name for review schema. */
    publisherName: PropTypes.string,
    /** Field name used to populate the card header text. */
    headerField: PropTypes.string,
    /** Field name used to populate the card body text. */
    bodyField: PropTypes.string,
    /** Optional field name for an image URL on the review card. */
    imageField: PropTypes.string,
    /** Limit how many review cards are shown. */
    maxReviews: PropTypes.number,
    /** Display mode: carousel or grid. */
    displayMode: PropTypes.oneOf(['carousel', 'grid']),
    /** Enable swipe/drag interactions on touch devices. */
    draggable: PropTypes.bool,
    /** Image fit mode for carousel cards. */
    imgFit: PropTypes.oneOf(['contain', 'cover', 'fill']),
    /** Include JSON-LD ReviewSchema for SEO. */
    includeReviewSchema: PropTypes.bool,
};
export function ContentfulReviewsCarousel(props) {
    const config = usePixelatedConfig();
    const [cards, setCards] = useState([]);
    const [reviewSchemas, setReviewSchemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const contentfulConfig = config?.integrations?.contentful;
    const apiProps = {
        proxyURL: contentfulConfig?.proxyURL ?? undefined,
        base_url: contentfulConfig?.base_url ?? '',
        space_id: contentfulConfig?.space_id ?? '',
        environment: contentfulConfig?.environment ?? '',
        delivery_access_token: contentfulConfig?.delivery_access_token ?? '',
    };
    const headerField = props.headerField || 'description';
    const bodyField = props.bodyField || 'reviewer';
    const imageField = props.imageField;
    const displayMode = props.displayMode || 'carousel';
    const draggable = props.draggable !== false;
    const imgFit = props.imgFit || 'cover';
    const includeReviewSchema = props.includeReviewSchema !== false;
    useEffect(() => {
        const fetchReviews = async () => {
            if (!apiProps.base_url || !apiProps.space_id || !apiProps.environment || !apiProps.delivery_access_token) {
                setLoading(false);
                return;
            }
            try {
                const response = await getContentfulEntriesByType({
                    apiProps,
                    contentType: props.reviewContentType,
                });
                const items = Array.isArray(response?.items)
                    ? response.items.filter((item) => item?.sys?.contentType?.sys?.id === props.reviewContentType)
                    : [];
                const reviewCards = items.slice(0, props.maxReviews ?? items.length).map((item, index) => ({
                    index,
                    cardIndex: index,
                    cardLength: Math.max(items.length, 0),
                    headerText: item.fields?.[headerField] ?? '',
                    bodyText: item.fields?.[bodyField] ?? '',
                    image: imageField ? item.fields?.[imageField] ?? '' : '',
                    imageAlt: item.fields?.[headerField] ?? '',
                    imgFit,
                }));
                setCards(reviewCards);
                if (includeReviewSchema) {
                    const schemas = await getContentfulReviewsSchema({
                        apiProps,
                        itemName: props.itemName,
                        itemType: props.itemType,
                        publisherName: props.publisherName,
                    });
                    setReviewSchemas(schemas ?? []);
                }
            }
            catch (e) {
                setError(e?.message || 'Failed to fetch reviews');
            }
            setLoading(false);
        };
        fetchReviews();
    }, [apiProps.base_url, apiProps.space_id, apiProps.environment, apiProps.delivery_access_token, props.reviewContentType, headerField, bodyField, imageField, props.maxReviews, includeReviewSchema, props.itemName, props.itemType, props.publisherName]);
    if (loading) {
        return (_jsx("div", { className: "contentful-reviews-card", children: _jsx("p", { className: "loading", children: "Loading reviews..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: "contentful-reviews-card", children: _jsxs("p", { className: "error", children: ["Error: ", error] }) }));
    }
    if (cards.length === 0) {
        return (_jsx("div", { className: "contentful-reviews-card", children: _jsx("p", { className: "no-reviews", children: "No reviews found." }) }));
    }
    return (_jsxs("div", { className: "contentful-reviews-card", children: [includeReviewSchema && reviewSchemas.map((schema, index) => (_jsx(ReviewSchema, { review: schema }, index))), _jsx(Carousel, { cards: cards, draggable: draggable, imgFit: imgFit })] }));
}
